import React from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material';
import {
  Description as RegistraturaIcon,
  AccountBalance as GestiuneIcon,
  Event as ConcediiIcon,
  People as UtilizatoriIcon,
  Church as ParohiiIcon,
  TrendingUp,
  Assignment,
  Schedule,
  Person
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();

  const stats = [
    {
      title: 'Documente înregistrate',
      value: '156',
      icon: <Assignment />,
      color: '#1976d2',
      change: '+12%'
    },
    {
      title: 'Tranzacții lunare',
      value: '€24,500',
      icon: <TrendingUp />,
      color: '#2e7d32',
      change: '+8%'
    },
    {
      title: 'Concedii active',
      value: '8',
      icon: <Schedule />,
      color: '#ed6c02',
      change: '-2'
    },
    {
      title: 'Utilizatori activi',
      value: '24',
      icon: <Person />,
      color: '#9c27b0',
      change: '+3'
    }
  ];

  const recentActivities = [
    { id: 1, text: 'Document nou înregistrat: Cerere de căsătorie', time: '2 ore în urmă' },
    { id: 2, text: 'Tranzacție adăugată: Donatie parohie', time: '4 ore în urmă' },
    { id: 3, text: 'Concediu aprobat: Părinte 1', time: '1 zi în urmă' },
    { id: 4, text: 'Utilizator nou: Secretar parohie', time: '2 zile în urmă' }
  ];

  const quickActions = [
    {
      title: 'Registratura',
      description: 'Gestionează documentele și corespondența',
      icon: <RegistraturaIcon />,
      path: '/registratura',
      permission: 'registratura.read'
    },
    {
      title: 'Gestiune',
      description: 'Administrează finanțele și inventarul',
      icon: <GestiuneIcon />,
      path: '/gestiune',
      permission: 'gestiune.read'
    },
    {
      title: 'Concedii',
      description: 'Gestionează concediile personalului',
      icon: <ConcediiIcon />,
      path: '/concedii',
      permission: 'concedii.read'
    },
    {
      title: 'Parohii',
      description: 'Administrează parohiile din subordine',
      icon: <ParohiiIcon />,
      path: '/parohii',
      permission: 'parohii.read'
    },
    {
      title: 'Utilizatori',
      description: 'Administrează utilizatorii sistemului',
      icon: <UtilizatoriIcon />,
      path: '/administrare-utilizatori',
      permission: 'utilizatori.read'
    }
  ];

  const filteredQuickActions = quickActions.filter(action => 
    !action.permission || hasPermission(action.permission)
  );

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Bun venit, {user?.name}!
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        {user?.role === 'episcop' ? 'Episcop' : 
         user?.role === 'vicar' ? 'Vicar' :
         user?.role === 'paroh' ? 'Paroh' :
         user?.role === 'secretar' ? 'Secretar' :
         user?.role === 'contabil' ? 'Contabil' : 'Utilizator'}
      </Typography>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        {/* Statistici */}
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      backgroundColor: `${stat.color}20`,
                      color: stat.color,
                      mr: 2
                    }}
                  >
                    {stat.icon}
                  </Box>
                  <Box>
                    <Typography variant="h6" component="div">
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.title}
                    </Typography>
                  </Box>
                </Box>
                <Chip
                  label={stat.change}
                  size="small"
                  color={stat.change.startsWith('+') ? 'success' : 'default'}
                />
              </CardContent>
            </Card>
          </Grid>
        ))}

        {/* Acțiuni rapide */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Acțiuni rapide
            </Typography>
            <Grid container spacing={2}>
              {filteredQuickActions.map((action, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ mr: 2, color: 'primary.main' }}>
                          {action.icon}
                        </Box>
                        <Typography variant="h6">
                          {action.title}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {action.description}
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button
                        size="small"
                        onClick={() => navigate(action.path)}
                      >
                        Deschide
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        {/* Activități recente */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Activități recente
            </Typography>
            <List>
              {recentActivities.map((activity, index) => (
                <React.Fragment key={activity.id}>
                  <ListItem alignItems="flex-start">
                    <ListItemIcon>
                      <Assignment color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={activity.text}
                      secondary={activity.time}
                    />
                  </ListItem>
                  {index < recentActivities.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
